import { useI18n } from 'vue-i18n';
import { reactive } from 'vue';
import { Attribute, MarketingBanner } from '../../../types.js';

export const errorCodes = {
    hardwareManagementUnavailable: 'HARDWARE_MANAGEMENT_UNAVAILABLE',
    hardwareManagementUnauthorized: 'HARDWARE_MANAGEMENT_UNAUTHORIZED',
    hardwareManagementError: 'HARDWARE_MANAGEMENT_ERROR',
};
export const NO_DATA_SYMBOL = '–';

const coreState = reactive({
    csrf: '',
    stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
    pages: {
        privacyUrl: undefined as string | undefined,
    },
    statamicPages: {
        terms: undefined as string | undefined,
        blog: undefined as string | undefined,
    },
    marketingBanners: [] as MarketingBanner[],
    isFeatureNewsPostsAvailable:
        import.meta.env.VITE_VUE_IS_FEATURE_NEWS_POSTS_AVAILABLE === 'true' ? true : false,
    isFeatureTicketsAvailable:
        import.meta.env.VITE_VUE_IS_FEATURE_TICKETS_AVAILABLE === 'true' ? true : false,
    isFeatureOrderApproveAvailable:
        import.meta.env.VITE_VUE_IS_FEATURE_ORDER_APPROVE_AVAILABLE === 'true' ? true : false,
    isFeatureProtectAvailable:
        import.meta.env.VITE_VUE_IS_FEATURE_PROTECT_AVAILABLE === 'true' ? true : false,
    isFeatureNotificationSystemAvailable:
        import.meta.env.VITE_VUE_IS_FEATURE_NOTIFICATION_SYSTEM_AVAILABLE === 'true' ? true : false,
    isFeatureDatacenterVisitAvailable:
        import.meta.env.VITE_VUE_IS_FEATURE_DATACENTER_VISIT_AVAILABLE === 'true' ? true : false,
    isFeatureInvoicePaymentAvailable:
        import.meta.env.VITE_VUE_IS_FEATURE_INVOICE_PAYMENT_AVAILABLE === 'true' ? true : false,
    isFeatureInboxAvailable:
        import.meta.env.VITE_VUE_IS_FEATURE_INBOX_AVAILABLE === 'true' ? true : false,

    features: {
        protectReleaseDate: new Date(import.meta.env.VITE_VUE_FEATURE_PROTECT_RELEASE_DATE),
    },

    isMobileSidebarOpen: false,
    isMaintenanceModeActive: false,
    isSessionExpired: false,
    isPasswordConfirmationRequired: false,
    isPasswordConfirmed: null as boolean | null,
    isForbidden: false,
    openDrawers: new Map<string, string>() as Map<string, string>,
});

const waitForValueToChange = (valueGetter, checkInterval = 100) =>
    new Promise((resolve) => {
        let currentValue = valueGetter();
        const intervalId = setInterval(() => {
            const newValue = valueGetter();
            if (newValue !== currentValue) {
                currentValue = newValue;
                clearInterval(intervalId);
                resolve(currentValue);
            }
        }, checkInterval);
    });

export function useCoreSetup() {
    const { t } = useI18n();

    function diffInWords(date1: Date | string, date2: Date | string | null = null) {
        let dateStart = new Date();
        let dateEnd = new Date();

        if (date2 === null) {
            date2 = date1;
            date1 = new Date();
        }

        if (typeof date1 === 'string') dateStart = new Date(date1);
        if (typeof date2 === 'string') dateEnd = new Date(date2);

        const diffTime = Math.abs(dateEnd.getTime() - dateStart.getTime());

        const diffInSeconds = Math.floor((diffTime / 1000) % 60);
        const diffInMinutes = Math.floor((diffTime / 1000 / 60) % 60);
        const diffInHours = Math.floor((diffTime / (1000 * 60 * 60)) % 24);
        const diffInDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const diffInWeeks = Math.floor(diffInDays / 7);
        const diffInMonths = Math.floor(diffInDays / 30);

        if (diffInMonths > 0) {
            return t('general.diffInMonths', { count: diffInMonths });
        } else if (diffInWeeks > 0) {
            return t('general.diffInWeeks', { count: diffInWeeks });
        } else if (diffInDays > 0) {
            return t('general.diffInDays', { count: diffInDays });
        } else if (diffInHours > 0) {
            return t('general.diffInHours', { count: diffInHours });
        } else if (diffInMinutes > 0) {
            return t('general.diffInMinutes', { count: diffInMinutes });
        } else {
            return t('general.diffInSeconds', { count: diffInSeconds });
        }
    }

    function buildAttributes(attributes: Attribute[], name: string): Attribute[] {
        return attributes
            .map((attribute: Attribute) => ({
                ...attribute,
                label: t(`resources.${name}.attributes.${attribute.name}`),
            }))
            .sort((a, b) => a.position - b.position);
    }

    return {
        coreState,
        buildAttributes,
        diffInWords,
    };
}

export function useCore() {
    const updatePageTitle = (title: string) => {
        if (title) {
            document.title = `${title} | 23M`;
        } else {
            document.title = '23M';
        }
    };

    const openSidebarMobile = () => {
        coreState.isMobileSidebarOpen = true;
    };

    const closeSidebarMobile = () => {
        coreState.isMobileSidebarOpen = false;
    };

    const confirmPassword = async () => {
        try {
            coreState.isPasswordConfirmationRequired = true;
            const isPasswordConfirmed = await waitForValueToChange(
                () => coreState.isPasswordConfirmed
            );
            return Promise.resolve(isPasswordConfirmed);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const openDrawer = (group: string, instanceId: string) => {
        coreState.openDrawers.set(group, instanceId);
    };

    const closeDrawer = (group: string) => {
        coreState.openDrawers.delete(group);
    };

    const getActiveDrawerInGroup = (group: string): string | undefined => {
        return coreState.openDrawers.get(group);
    };

    return {
        coreState,
        updatePageTitle,
        openSidebarMobile,
        closeSidebarMobile,
        confirmPassword,
        openDrawer,
        closeDrawer,
        getActiveDrawerInGroup,
    };
}
